// Sidebar navigazione laterale con collapse/expand e ruoli
import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, Ticket, FileText, Users, Building2,
  Package, MapPin, Shield, BarChart2, Trophy, Puzzle,
  Settings, HelpCircle, ChevronLeft, ChevronRight, Layers, UserCog,
  ClipboardCheck, Wrench, ChevronDown, CalendarDays, MessageSquare, Briefcase,
  CalendarCheck, Smartphone, ExternalLink, TicketPlus,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAuth } from '../../hooks/useAuth'

interface NavItem {
  to: string
  label: string
  icon: React.ReactNode
  roles?: string[]
  children?: { to: string; label: string }[]
  external?: boolean  // apre in nuova tab
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: <LayoutDashboard className="h-5 w-5" /> },
  { to: '/tickets', label: 'Ticket', icon: <Ticket className="h-5 w-5" /> },
  { to: '/reports', label: 'Rapportini', icon: <FileText className="h-5 w-5" /> },
  {
    to: '/my-company',
    label: 'La mia azienda',
    icon: <Briefcase className="h-5 w-5" />,
    roles: ['admin', 'super_admin'],
    children: [
      { to: '/my-company/branches', label: 'Filiali' },
    ],
  },
  { to: '/companies', label: 'Clienti', icon: <Building2 className="h-5 w-5" />, roles: ['admin', 'super_admin', 'dispatcher', 'team_leader', 'readonly'] },
  { to: '/warehouse', label: 'Magazzino', icon: <Package className="h-5 w-5" /> },
  {
    to: '/technicians',
    label: 'Tecnici',
    icon: <Users className="h-5 w-5" />,
    roles: ['admin', 'super_admin', 'dispatcher', 'team_leader'],
    children: [
      { to: '/technicians', label: 'Lista tecnici' },
      { to: '/technicians/calendar', label: 'Calendario' },
    ],
  },
  {
    to: '/presenze',
    label: 'Presenze',
    icon: <CalendarCheck className="h-5 w-5" />,
    roles: ['admin', 'super_admin', 'team_leader'],
    children: [
      { to: '/presenze', label: 'Calendario team' },
      { to: '/presenze/richieste', label: 'Approvazioni' },
    ],
  },
  {
    to: '/permessi',
    label: 'I miei permessi',
    icon: <CalendarCheck className="h-5 w-5" />,
    roles: ['technician', 'team_leader'],
  },
  {
    to: '/maintenance',
    label: 'Manutenzione',
    icon: <ClipboardCheck className="h-5 w-5" />,
    roles: ['admin', 'super_admin', 'dispatcher', 'team_leader'],
    children: [
      { to: '/maintenance/checklists', label: 'Checklist' },
      { to: '/maintenance/assets',     label: 'Parco macchine' },
    ],
  },
  { to: '/hse', label: 'HSE', icon: <Shield className="h-5 w-5" /> },
  { to: '/geolocation', label: 'Mappa live', icon: <MapPin className="h-5 w-5" />, roles: ['admin', 'super_admin', 'dispatcher', 'team_leader'] },
  { to: '/chat', label: 'Chat interna', icon: <MessageSquare className="h-5 w-5" /> },
  { to: '/analytics', label: 'Analytics', icon: <BarChart2 className="h-5 w-5" />, roles: ['admin', 'super_admin', 'team_leader'] },
  { to: '/gamification', label: 'Gamification', icon: <Trophy className="h-5 w-5" /> },
  { to: '/digital-twin', label: 'Digital Twin', icon: <Layers className="h-5 w-5" />, roles: ['admin', 'super_admin', 'dispatcher'] },
  { to: '/integrations', label: 'Integrazioni', icon: <Puzzle className="h-5 w-5" />, roles: ['admin', 'super_admin'] },
  { to: '/admin/users', label: 'Utenti', icon: <UserCog className="h-5 w-5" />, roles: ['admin', 'super_admin'] },
]

const BOTTOM_ITEMS: NavItem[] = [
  {
    to: '/richiesta',
    label: 'Apertura ticket clienti',
    icon: <TicketPlus className="h-5 w-5" />,
    external: true,
    roles: ['admin', 'super_admin', 'dispatcher', 'team_leader'],
  },
  { to: '/mobile', label: 'App Mobile', icon: <Smartphone className="h-5 w-5" /> },
  { to: '/settings', label: 'Impostazioni', icon: <Settings className="h-5 w-5" /> },
  { to: '/help', label: 'Aiuto', icon: <HelpCircle className="h-5 w-5" /> },
]

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { hasRole } = useAuth()
  const location = useLocation()
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({ '/maintenance': true, '/technicians': false, '/my-company': true })

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/'
    return location.pathname.startsWith(to)
  }

  const toggleGroup = (to: string) => {
    setOpenGroups(p => ({ ...p, [to]: !p[to] }))
  }

  const filteredItems = NAV_ITEMS.filter(
    (item) => !item.roles || hasRole(item.roles)
  )

  const renderItem = (item: NavItem) => {
    const hasChildren = item.children && item.children.length > 0
    const groupActive = isActive(item.to)
    const isOpen = openGroups[item.to] ?? false

    if (hasChildren && !collapsed) {
      return (
        <li key={item.to}>
          {/* Group header — clickable toggle */}
          <button
            onClick={() => toggleGroup(item.to)}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              'text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-surface-overlay)] hover:text-[var(--fc-foreground)]',
              groupActive && 'bg-[color-mix(in_srgb,var(--fc-primary)_10%,transparent)] text-[var(--fc-primary)]',
            )}
          >
            <span className="shrink-0">{item.icon}</span>
            <span className="flex-1 text-left">{item.label}</span>
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-180')} />
          </button>

          {/* Sub-items */}
          {isOpen && (
            <ul className="mt-0.5 ml-4 space-y-0.5 border-l border-[var(--fc-border)] pl-3">
              {item.children!.map(child => (
                <li key={child.to}>
                  <NavLink
                    to={child.to}
                    className={cn(
                      'flex items-center rounded-md px-2 py-1.5 text-sm transition-colors',
                      'text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-surface-overlay)] hover:text-[var(--fc-foreground)]',
                      location.pathname === child.to && 'font-medium text-[var(--fc-primary)]',
                    )}
                  >
                    {child.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </li>
      )
    }

    // Flat item (or collapsed with children → show parent icon only)
    // Link esterno — apre in nuova tab
    if (item.external) {
      return (
        <li key={item.to}>
          <a
            href={item.to}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              'text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-surface-overlay)] hover:text-[var(--fc-foreground)]',
              collapsed && 'justify-center px-2'
            )}
            title={collapsed ? item.label : undefined}
          >
            <span className="shrink-0">{item.icon}</span>
            {!collapsed && (
              <span className="flex flex-1 items-center gap-1">
                {item.label}
                <ExternalLink className="h-3 w-3 opacity-50" />
              </span>
            )}
          </a>
        </li>
      )
    }

    return (
      <li key={item.to}>
        <NavLink
          to={hasChildren ? (item.children![0].to) : item.to}
          end={item.to === '/'}
          className={cn(
            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            'text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-surface-overlay)] hover:text-[var(--fc-foreground)]',
            isActive(item.to) && 'bg-[color-mix(in_srgb,var(--fc-primary)_10%,transparent)] text-[var(--fc-primary)]',
            collapsed && 'justify-center px-2'
          )}
          title={collapsed ? item.label : undefined}
          aria-current={isActive(item.to) ? 'page' : undefined}
        >
          <span className="shrink-0">{item.icon}</span>
          {!collapsed && <span>{item.label}</span>}
        </NavLink>
      </li>
    )
  }

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 flex h-screen flex-col border-r border-[var(--fc-border)] bg-[var(--fc-surface)] transition-[width] duration-200',
        collapsed ? 'w-16' : 'w-60'
      )}
      aria-label="Navigazione principale"
    >
      {/* Logo */}
      <div className={cn(
        'flex h-16 items-center border-b border-[var(--fc-border)] px-4',
        collapsed ? 'justify-center' : 'justify-between'
      )}>
        {!collapsed && (
          <span className="text-lg font-bold tracking-tight text-[var(--fc-foreground)]">KleisNote</span>
        )}
        {collapsed && (
          <span className="text-sm font-bold text-[var(--fc-foreground)]">K</span>
        )}
        <button
          onClick={onToggle}
          className={cn(
            'rounded p-1 text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-surface-overlay)] hover:text-[var(--fc-foreground)] transition-colors',
            collapsed && 'hidden'
          )}
          aria-label={collapsed ? 'Espandi sidebar' : 'Riduci sidebar'}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      </div>

      {/* Nav principale */}
      <nav className="flex-1 overflow-y-auto py-3 px-2" aria-label="Menu principale">
        <ul className="space-y-0.5" role="list">
          {filteredItems.map(renderItem)}
        </ul>
      </nav>

      {/* Bottom nav */}
      <div className="border-t border-[var(--fc-border)] px-2 py-3">
        <ul className="space-y-0.5" role="list">
          {BOTTOM_ITEMS.filter(item => !item.roles || hasRole(item.roles)).map(renderItem)}
        </ul>
        {collapsed && (
          <button
            onClick={onToggle}
            className="mt-2 flex w-full justify-center rounded-lg px-2 py-2 text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-surface-overlay)] hover:text-[var(--fc-foreground)] transition-colors"
            aria-label="Espandi sidebar"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </aside>
  )
}
